import { useI18nContext } from '@i18n/i18n-react';
import { DashboardRepresentation } from '@common/api';
import { Update } from '@use-gpu/state';
import { Button } from '@components/button';
import { Popover } from '@components/popover/Popover';
import { SwitchField } from '@components/form';
import { Text } from '@components/typography/Typography';
import { FlexCol, FlexRow } from '@components/layout/Flex';
import { DeepRequired, $toggle } from '@util/patch';
import { ThreeDots } from '@components/icons/FarseerIcons';
import { GridLayoutIgnoreDrag } from '@components/grid-layout';

export type DashboardOptionsProps = {
    dashboard: DashboardRepresentation;
    updateDashboard: (u: Update<DeepRequired<DashboardRepresentation>>) => void;
};

export const DashboardOptions = (props: DashboardOptionsProps) => {
    const { LL } = useI18nContext();

    const { dashboard, updateDashboard } = props;
    const {
        options: { gridLocked, responsiveLayout, autoRefresh = true },
        columns: {
            intervalToolbar: { enabled: intervalToolbarEnabled },
        },
    } = dashboard;

    const actions = {
        toggleTimeline: () => updateDashboard({ columns: { intervalToolbar: { enabled: $toggle() } } }),
        toggleGridLocked: () => updateDashboard({ options: { gridLocked: $toggle() } }),
        toggleResponsiveLayout: () => updateDashboard({ options: { responsiveLayout: $toggle() } }),
        toggleAutoRefresh: () => updateDashboard({ options: { autoRefresh: $toggle() } }),
    };

    const dashboardOptions = (
        <FlexCol>
            <SwitchField label={LL.TIMELINE()} value={intervalToolbarEnabled} onChange={actions.toggleTimeline} />
            <SwitchField label={LL.LOCK_GRID()} value={gridLocked} onChange={actions.toggleGridLocked} />
            <SwitchField
                label={LL.RESPONSIVE_LAYOUT()}
                value={responsiveLayout}
                onChange={actions.toggleResponsiveLayout}
            />
            <SwitchField label={LL.AUTO_REFRESH_DASHBOARD()} value={autoRefresh} onChange={actions.toggleAutoRefresh} />
        </FlexCol>
    );

    return (
        <Popover placement="bottom-end" mainOffset={5}>
            <Popover.Trigger>
                <Button $size="small" $type="quaternary" icon={ThreeDots} />
            </Popover.Trigger>
            <Popover.Content width={220} $padding="8px 16px">
                <GridLayoutIgnoreDrag>
                    <FlexRow style={{ minHeight: 32, flexGrow: 1, alignItems: 'center' }}>
                        <Text $bold>{LL.DASHBOARD_OPTIONS()}</Text>
                    </FlexRow>
                    {dashboardOptions}
                </GridLayoutIgnoreDrag>
            </Popover.Content>
        </Popover>
    );
};
